import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";

import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AirplanemodeActiveOutlinedIcon from "@mui/icons-material/AirplanemodeActiveOutlined";
import AirplanemodeInactiveOutlinedIcon from "@mui/icons-material/AirplanemodeInactiveOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";

const IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";

const EMPLOYEE_TYPES = [
  { value: "permanent", label: "Permanent" },
  { value: "temporary", label: "Temporary" },
  { value: "contractor", label: "Contract" },
  { value: "founder", label: "Founder" },
  { value: "intern", label: "Internship" },
  { value: "communication", label: "Communication" },
  { value: "receptionist", label: "Receptionist" },
];

const ROLES = ["admin", "hr", "manager", "supervisor", "user"];

const EMPTY_FORM = {
  FirstName: "",
  LastName: "",
  email: "",
  Password: "",
  Cell: "",
  employeeType: "permanent",
  department: "",
  physicalAddress: [{ street: "", city: "", town: "", country: "" }],
  postalAddress: [{ code: "", city: "", town: "", country: "" }],
  date_of_birth: [{ day: "", month: "", year: "" }],
  nationality: "",
  national_id: "",
  next_of_kin: [{ fullname: "", email: "", cell: "" }],
  role: "user",
  role_explanation: "",
  drive_quote: "",
  passion: "",
  knowledge: "",
  core_values: "",
  focus: "",
  status: "active",
  isVerified: "0",
  isLogedIn: "0",
  email_verified: "0",
  verified_at: "",
  image: null,
};

/* ─── Helpers ──────────────────────────────────────────────────────────── */

const parseField = (value, fallback) => {
  if (!value) return fallback;
  if (Array.isArray(value)) return value.length ? value : fallback;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) && parsed.length ? parsed : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const isActiveStaff = (s) =>
  s?.status === "active" || s?.status === 1 || s?.status === "1";

const isFlagged = (v) => v === "1" || v === 1 || v === true;

const imageUrl = (img) => (img ? `${IMAGE_BASE}${img}` : "");

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const dobText = (staff) => {
  const dob = parseField(staff?.date_of_birth, null);
  if (!dob?.[0]) return "—";
  const { day, month, year } = dob[0];
  if (!day && !month && !year) return "—";
  return `${day || "?"}/${month || "?"}/${year || "?"}`;
};

const addressText = (raw, keys) => {
  const parsed = parseField(raw, null);
  if (!parsed?.[0]) return "—";
  const parts = keys.map((k) => parsed[0][k]).filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
};

/* ─────────────────────────────────────────────────────────────────────── */

/* ─── Presentational components ────────────────────────────────────────── */
// Defined at module scope on purpose. A component declared inside Team would be a
// brand-new type on every render, so React would unmount and remount these
// subtrees on each keystroke and the focused input would lose focus after one
// character.

const FormSection = ({ ui, title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="h6"
      sx={{
        mb: 1.5,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        fontWeight: 700,
        color: ui.accent,
      }}
    >
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const DetailRow = ({ ui, label, value, color }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="h6"
      sx={{
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: ui.textSecondary,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{ fontWeight: 600, color: color || ui.textPrimary, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const NarrativeBlock = ({ ui, isDark, title, text }) =>
  text ? (
    <Box
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${ui.border}`,
        bgcolor: alpha(ui.surfaceAlt, isDark ? 0.5 : 0.7),
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 0.75,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontWeight: 700,
          color: ui.accent,
        }}
      >
        {title}
      </Typography>
      <Typography variant="h5" sx={{ lineHeight: 1.65, color: ui.textPrimary }}>
        {text}
      </Typography>
    </Box>
  ) : null;

/* ─────────────────────────────────────────────────────────────────────── */

const Team = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { getData, addData, updateData } = useAuth();

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState("newest");

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [staffToDeactivate, setStaffToDeactivate] = useState(null);

  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  const roleColor = useCallback(
    (role) =>
      ({
        admin: colors.red[500],
        hr: colors.newTeal[500],
        manager: colors.orange[500],
        supervisor: colors.green[500],
        user: colors.indigo[500],
      }[role] || colors.gray[500]),
    [colors]
  );

  const roleIcon = (role) => {
    const props = { sx: { fontSize: 15 } };
    switch (role) {
      case "admin":
        return <AdminPanelSettingsOutlinedIcon {...props} />;
      case "manager":
        return <SecurityOutlinedIcon {...props} />;
      case "hr":
        return <BadgeOutlinedIcon {...props} />;
      case "supervisor":
        return <SupervisorAccountOutlinedIcon {...props} />;
      default:
        return <LockOpenOutlinedIcon {...props} />;
    }
  };

  /* ---------------- Data ---------------- */
  const fetchData = useCallback(async () => {
    try {
      const response = await getData("proccess.php", "staffs");
      if (response.success) setTeams(response.data || []);
      else toast.error(response.message || "Failed to load team data");
    } catch (err) {
      toast.error("Network error: Failed to fetch teams");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyTeamsResponse = useCallback(
    async (response) => {
      if (Array.isArray(response?.data)) setTeams(response.data);
      else await fetchData();
    },
    [fetchData]
  );

  /* ---------------- Filtering & sorting ---------------- */
  const visibleTeams = useMemo(() => {
    const q = search.trim().toLowerCase();

    return teams
      .filter((s) => {
        if (statusFilter === "all") return true;
        return statusFilter === "active" ? isActiveStaff(s) : !isActiveStaff(s);
      })
      .filter((s) => {
        if (!q) return true;
        return [
          s.FirstName,
          s.LastName,
          s.email,
          s.Cell,
          s.role,
          s.department,
          s.employeeType,
          s.nationality,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (sortBy === "name") {
          return `${a.FirstName} ${a.LastName}`.localeCompare(
            `${b.FirstName} ${b.LastName}`
          );
        }
        const byDate = new Date(b.createDate || 0) - new Date(a.createDate || 0);
        if (byDate !== 0) return byDate;
        return Number(b.Id) - Number(a.Id);
      });
  }, [teams, search, statusFilter, sortBy]);

  const counts = useMemo(
    () => ({
      active: teams.filter(isActiveStaff).length,
      inactive: teams.filter((s) => !isActiveStaff(s)).length,
      all: teams.length,
    }),
    [teams]
  );

  /* ---------------- Export ---------------- */
  const exportToExcel = () => {
    if (visibleTeams.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const rows = visibleTeams.map((staff) => ({
        ID: staff.Id,
        "First Name": staff.FirstName || "",
        "Last Name": staff.LastName || "",
        Email: staff.email || "",
        Cell: staff.Cell || "",
        "Employee Type": staff.employeeType || "",
        Department: staff.department || "",
        Nationality: staff.nationality || "",
        "National ID": staff.national_id || "",
        "Next of Kin": parseField(staff.next_of_kin, null)?.[0]?.fullname || "",
        "Date of Birth": dobText(staff),
        Role: staff.role || "",
        "Account Status": staff.status || "",
        Verified: isFlagged(staff.isVerified) ? "Verified" : "Not Verified",
        "Last Login": staff.last_login || "",
        "Registration Date": staff.createDate || "",
        "Physical Address": addressText(staff.physicalAddress, [
          "street",
          "city",
          "town",
          "country",
        ]),
        "Postal Address": addressText(staff.postalAddress, [
          "code",
          "city",
          "town",
          "country",
        ]),
        "Role Explanation": staff.role_explanation || "",
        "Drive Quote": staff.drive_quote || "",
        Passion: staff.passion || "",
        Knowledge: staff.knowledge || "",
        "Core Values": staff.core_values || "",
        Focus: staff.focus || "",
      }));

      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(","),
        ...rows.map((row) =>
          headers
            .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `team_members_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${visibleTeams.length} member(s)`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  /* ---------------- Dialog handlers ---------------- */
  const handleOpenDialog = (isEdit = false, staff = null) => {
    setIsEditing(isEdit);
    setSelectedStaffId(isEdit ? staff.Id : null);

    if (isEdit && staff) {
      setFormData({
        ...EMPTY_FORM,
        ...staff,
        Password: "",
        employeeType: staff.employeeType || "permanent",
        department: staff.department || "",
        nationality: staff.nationality || "",
        national_id: staff.national_id || "",
        role_explanation: staff.role_explanation || "",
        drive_quote: staff.drive_quote || "",
        passion: staff.passion || "",
        knowledge: staff.knowledge || "",
        core_values: staff.core_values || "",
        focus: staff.focus || "",
        isLogedIn: staff.isLogedIn || "0",
        email_verified: staff.email_verified || "0",
        verified_at: staff.verified_at || "",
        physicalAddress: parseField(staff.physicalAddress, EMPTY_FORM.physicalAddress),
        postalAddress: parseField(staff.postalAddress, EMPTY_FORM.postalAddress),
        date_of_birth: parseField(staff.date_of_birth, EMPTY_FORM.date_of_birth),
        next_of_kin: parseField(staff.next_of_kin, EMPTY_FORM.next_of_kin),
        isVerified: isFlagged(staff.isVerified) ? "1" : "0",
        image: staff.image || null,
      });
      setImagePreview(staff.image ? imageUrl(staff.image) : null);
    } else {
      setFormData(EMPTY_FORM);
      setImagePreview(null);
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setImagePreview(null);
    setSelectedStaffId(null);
    setFormData(EMPTY_FORM);
  };

  /**
   * Keeps the File object on formData.image AND drives the preview.
   * NOTE: this only reaches the server if addData/updateData wrap the payload
   * in FormData — a File cannot survive JSON.stringify.
   */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, image: file }));

    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const nested = [
      { prefix: "physical", key: "physicalAddress" },
      { prefix: "postal", key: "postalAddress" },
      { prefix: "dob", key: "date_of_birth" },
      { prefix: "nextOfKin", key: "next_of_kin" },
    ].find((n) => name.startsWith(n.prefix));

    if (nested) {
      const field = name.slice(nested.prefix.length).toLowerCase();
      setFormData((prev) => ({
        ...prev,
        [nested.key]: [{ ...prev[nested.key][0], [field]: value }],
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);

    try {
      const payload = { ...formData };

      const blankOut = (key, fields) => {
        const obj = payload[key]?.[0] || {};
        if (fields.every((f) => !obj[f])) payload[key] = null;
        else payload[key] = JSON.stringify(payload[key]);
      };

      blankOut("physicalAddress", ["street", "city", "town", "country"]);
      blankOut("postalAddress", ["code", "city", "town", "country"]);
      blankOut("date_of_birth", ["day", "month", "year"]);
      blankOut("next_of_kin", ["fullname", "email", "cell"]);

      // Don't overwrite an existing password with an empty string.
      if (isEditing && !payload.Password) delete payload.Password;

      const response = isEditing
        ? await updateData("proccess.php", "staffs", payload, selectedStaffId)
        : await addData("proccess.php", "staffs", payload);

      if (response.success) {
        toast.success(isEditing ? "Staff updated" : "Staff created");
        await applyTeamsResponse(response);
        handleCloseDialog();
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Failed to save staff");
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  // Staff are never deleted - the account is deactivated so their history
  // (leave, assets, tasks) stays intact and they can be reinstated.
  // deleteData() would remove the row AND unlink their profile photo.
  const confirmDeactivate = async () => {
    const target = staffToDeactivate;
    const reinstating = !isActiveStaff(target);
    const newStatus = reinstating ? "active" : "inactive";

    try {
      const response = await updateData(
        "proccess.php",
        "staffs",
        { status: newStatus },
        target.Id
      );

      if (response.success) {
        toast.success(
          reinstating ? "Staff account reactivated" : "Staff account deactivated"
        );
        if (Array.isArray(response.data)) {
          setTeams(response.data);
        } else {
          setTeams((prev) =>
            prev.map((s) =>
              String(s.Id) === String(target.Id) ? { ...s, status: newStatus } : s
            )
          );
        }
        setSelectedUser((prev) =>
          prev && String(prev.Id) === String(target.Id)
            ? { ...prev, status: newStatus }
            : prev
        );
      } else {
        toast.error(response.message || "Failed to update the staff account");
      }
    } catch (err) {
      toast.error("Network error: Failed to update the staff account");
      console.error("Deactivate error:", err);
    }
    setStaffToDeactivate(null);
  };

  const handleVerifyToggle = async (staff) => {
    const newStatus = isFlagged(staff.isVerified) ? "0" : "1";
    try {
      const response = await updateData(
        "proccess.php",
        "staffs",
        { isVerified: newStatus },
        staff.Id
      );
      if (response.success) {
        toast.success(`Staff ${newStatus === "1" ? "verified" : "unverified"}`);
        await applyTeamsResponse(response);
        setSelectedUser((prev) =>
          prev && prev.Id === staff.Id ? { ...prev, isVerified: newStatus } : prev
        );
      } else {
        toast.error(response.message || "Failed to update verification status");
      }
    } catch (err) {
      toast.error("Network error: Failed to update verification status");
      console.error("Verify toggle error:", err);
    }
  };

  /* ---------------- Shared style helpers ---------------- */
  const initials = (s) =>
    `${s?.FirstName?.[0] || ""}${s?.LastName?.[0] || ""}`.toUpperCase() || "?";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
    "& .MuiFormHelperText-root": { color: ui.textSecondary },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  const outlineBtn = (color) => ({
    px: 2.5,
    py: 1.05,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
    "&:hover": { bgcolor: alpha(color, 0.1), borderColor: color },
  });

  /* ------------------------------------------------------------------ */
  const currentYear = new Date().getFullYear();

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        {/* ------------------------------------------------------------ */}
        {/* Header                                                        */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{ fontWeight: 800, letterSpacing: -0.4, color: ui.textPrimary }}
            >
              Our Team
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Click any member for full details and actions
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search name, email, role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 19, color: ui.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={outlineBtn(colors.green[500])}
            >
              Export
            </Button>
            <Button
              startIcon={<AddCircleOutlineOutlinedIcon />}
              onClick={() => handleOpenDialog(false)}
              sx={primaryBtn}
            >
              Add staff
            </Button>
          </Stack>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* Filters                                                       */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <ToggleButtonGroup
            exclusive
            value={statusFilter}
            onChange={(_, v) => v !== null && setStatusFilter(v)}
            sx={{
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0.75,
                border: `1px solid ${ui.border} !important`,
                borderRadius: "999px !important",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                color: ui.textSecondary,
                bgcolor: ui.surface,
                "&.Mui-selected": {
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.14),
                  borderColor: `${alpha(ui.accent, 0.5)} !important`,
                  "&:hover": { bgcolor: alpha(ui.accent, 0.2) },
                },
              },
            }}
          >
            {[
              { key: "active", label: "Current staff", count: counts.active },
              { key: "inactive", label: "Former staff", count: counts.inactive },
              { key: "all", label: "All", count: counts.all },
            ].map((f) => (
              <ToggleButton key={f.key} value={f.key}>
                {f.label}
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.85,
                    borderRadius: 999,
                    fontSize: 11,
                    bgcolor: alpha(ui.textSecondary, 0.18),
                  }}
                >
                  {f.count}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={sortBy}
            onChange={(_, v) => v !== null && setSortBy(v)}
            sx={{
              gap: 1,
              "& .MuiToggleButton-root": {
                px: 1.75,
                py: 0.6,
                border: `1px solid ${ui.border} !important`,
                borderRadius: "999px !important",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: ui.textSecondary,
                bgcolor: ui.surface,
                "&.Mui-selected": {
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.14),
                  borderColor: `${alpha(ui.accent, 0.5)} !important`,
                },
              },
            }}
          >
            <ToggleButton value="newest">Newest first</ToggleButton>
            <ToggleButton value="name">A–Z</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* Cards                                                         */}
        {/* ------------------------------------------------------------ */}
        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`sk-${i}`}>
                <Skeleton
                  variant="rounded"
                  height={252}
                  sx={{ borderRadius: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
                />
              </Grid>
            ))}
          </Grid>
        ) : visibleTeams.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
              No team members found
            </Typography>
            <Typography variant="h6" sx={{ color: ui.textSecondary }}>
              {search ? "Nothing matches your search." : "Add a staff member to begin."}
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={2.5}>
            {visibleTeams.map((staff) => {
              const rColor = roleColor(staff.role);
              const active = isActiveStaff(staff);
              const verified = isFlagged(staff.isVerified);

              return (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={staff.Id}>
                  <Paper
                    elevation={0}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedUser(staff)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedUser(staff);
                      }
                    }}
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      borderRadius: 3,
                      overflow: "hidden",
                      textAlign: "center",
                      bgcolor: ui.surface,
                      border: `1px solid ${ui.border}`,
                      boxShadow: ui.shadow,
                      opacity: active ? 1 : 0.72,
                      transition: "transform .2s ease, border-color .2s ease, box-shadow .2s ease",
                      "&:hover, &:focus-visible": {
                        outline: "none",
                        transform: "translateY(-4px)",
                        borderColor: alpha(rColor, 0.5),
                        boxShadow: `0 16px 32px -20px ${alpha(rColor, 0.9)}`,
                      },
                    }}
                  >
                    {/* Banner */}
                    <Box
                      sx={{
                        height: 62,
                        background: `linear-gradient(135deg, ${alpha(
                          rColor,
                          isDark ? 0.3 : 0.22
                        )} 0%, ${alpha(ui.accent2, isDark ? 0.18 : 0.12)} 100%)`,
                      }}
                    />

                    <Box sx={{ px: 2.5, pb: 2.5, mt: -4.5 }}>
                      <Box sx={{ position: "relative", display: "inline-block" }}>
                        <Avatar
                          src={imageUrl(staff.image)}
                          sx={{
                            width: 76,
                            height: 76,
                            fontSize: 24,
                            fontWeight: 700,
                            bgcolor: ui.surfaceAlt,
                            color: ui.textPrimary,
                            border: `3px solid ${ui.surface}`,
                            boxShadow: ui.shadow,
                          }}
                        >
                          {initials(staff)}
                        </Avatar>

                        {verified && (
                          <Tooltip title="Verified">
                            <VerifiedOutlinedIcon
                              sx={{
                                position: "absolute",
                                right: 2,
                                bottom: 2,
                                fontSize: 20,
                                color: colors.green[500],
                                bgcolor: ui.surface,
                                borderRadius: "50%",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography
                        variant="h4"
                        noWrap
                        sx={{ mt: 1.25, fontWeight: 700, color: ui.textPrimary }}
                      >
                        {staff.FirstName} {staff.LastName}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        justifyContent="center"
                        sx={{ mt: 1, mb: 1.75, flexWrap: "wrap", gap: 0.75 }}
                      >
                        <Chip
                          size="small"
                          icon={roleIcon(staff.role)}
                          label={staff.role || "user"}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            textTransform: "capitalize",
                            color: rColor,
                            bgcolor: alpha(rColor, 0.14),
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                        {!active && (
                          <Chip
                            size="small"
                            label="Former"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              color: colors.red[500],
                              bgcolor: alpha(colors.red[500], 0.14),
                            }}
                          />
                        )}
                      </Stack>

                      <Divider sx={{ borderColor: ui.border, mb: 1.5 }} />

                      <Stack spacing={0.85} sx={{ textAlign: "left" }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MailOutlineIcon
                            sx={{ fontSize: 15, color: ui.textSecondary, flexShrink: 0 }}
                          />
                          <Typography variant="h6" noWrap sx={{ color: ui.textSecondary }}>
                            {staff.email || "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIphoneIcon
                            sx={{ fontSize: 15, color: ui.textSecondary, flexShrink: 0 }}
                          />
                          <Typography variant="h6" noWrap sx={{ color: ui.textSecondary }}>
                            {staff.Cell || "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BadgeOutlinedIcon
                            sx={{ fontSize: 15, color: ui.textSecondary, flexShrink: 0 }}
                          />
                          <Typography variant="h6" noWrap sx={{ color: ui.textSecondary }}>
                            {staff.department || "No department"}
                            {staff.employeeType ? ` · ${staff.employeeType}` : ""}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Detail dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        {selectedUser && (
          <>
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2.25,
                background: `linear-gradient(180deg, ${alpha(
                  roleColor(selectedUser.role),
                  isDark ? 0.18 : 0.11
                )} 0%, transparent 100%)`,
              }}
            >
              <Avatar
                src={imageUrl(selectedUser.image)}
                sx={{
                  width: 78,
                  height: 78,
                  fontSize: 26,
                  fontWeight: 700,
                  bgcolor: ui.surfaceAlt,
                  color: ui.textPrimary,
                  border: `2px solid ${alpha(roleColor(selectedUser.role), 0.5)}`,
                }}
              >
                {initials(selectedUser)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
                  {selectedUser.FirstName} {selectedUser.LastName}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.85, flexWrap: "wrap", gap: 0.75 }}
                >
                  <Chip
                    size="small"
                    icon={roleIcon(selectedUser.role)}
                    label={selectedUser.role}
                    sx={{
                      fontWeight: 700,
                      textTransform: "capitalize",
                      color: roleColor(selectedUser.role),
                      bgcolor: alpha(roleColor(selectedUser.role), 0.14),
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                  <Chip
                    size="small"
                    label={isActiveStaff(selectedUser) ? "Active" : "Former staff"}
                    sx={{
                      fontWeight: 700,
                      color: isActiveStaff(selectedUser)
                        ? colors.green[500]
                        : colors.red[500],
                      bgcolor: alpha(
                        isActiveStaff(selectedUser) ? colors.green[500] : colors.red[500],
                        0.14
                      ),
                    }}
                  />
                  <Chip
                    size="small"
                    label={isFlagged(selectedUser.isVerified) ? "Verified" : "Unverified"}
                    sx={{
                      fontWeight: 700,
                      color: isFlagged(selectedUser.isVerified)
                        ? colors.green[500]
                        : ui.textSecondary,
                      bgcolor: alpha(
                        isFlagged(selectedUser.isVerified)
                          ? colors.green[500]
                          : ui.textSecondary,
                        0.14
                      ),
                    }}
                  />
                </Stack>
              </Box>

              <IconButton
                onClick={() => setSelectedUser(null)}
                sx={{
                  color: ui.textSecondary,
                  "&:hover": { color: colors.red[500], bgcolor: alpha(colors.red[500], 0.12) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Contact & employment
                  </Typography>
                  <DetailRow ui={ui} label="Email" value={selectedUser.email} />
                  <DetailRow ui={ui} label="Cell" value={selectedUser.Cell} />
                  <DetailRow ui={ui} label="Department" value={selectedUser.department} />
                  <DetailRow ui={ui} label="Employee type" value={selectedUser.employeeType} />
                  <DetailRow ui={ui} label="Nationality" value={selectedUser.nationality} />
                  <DetailRow ui={ui} label="National ID" value={selectedUser.national_id} />
                  <DetailRow ui={ui} label="Date of birth" value={dobText(selectedUser)} />
                  <DetailRow ui={ui}
                    label="Registered"
                    value={fmtDate(selectedUser.createDate)}
                  />
                  <DetailRow ui={ui}
                    label="Last login"
                    value={fmtDate(selectedUser.last_login)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Addresses & next of kin
                  </Typography>
                  <DetailRow ui={ui}
                    label="Physical address"
                    value={addressText(selectedUser.physicalAddress, [
                      "street",
                      "city",
                      "town",
                      "country",
                    ])}
                  />
                  <DetailRow ui={ui}
                    label="Postal address"
                    value={addressText(selectedUser.postalAddress, [
                      "code",
                      "city",
                      "town",
                      "country",
                    ])}
                  />
                  {(() => {
                    const nok = parseField(selectedUser.next_of_kin, null)?.[0];
                    return (
                      <>
                        <DetailRow ui={ui} label="Next of kin" value={nok?.fullname} />
                        <DetailRow ui={ui} label="Next of kin email" value={nok?.email} />
                        <DetailRow ui={ui} label="Next of kin cell" value={nok?.cell} />
                      </>
                    );
                  })()}
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <NarrativeBlock ui={ui} isDark={isDark}
                    title="Role explanation"
                    text={selectedUser.role_explanation}
                  />
                  <NarrativeBlock ui={ui} isDark={isDark} title="Drive quote" text={selectedUser.drive_quote} />
                  <NarrativeBlock ui={ui} isDark={isDark} title="Passion" text={selectedUser.passion} />
                  <NarrativeBlock ui={ui} isDark={isDark} title="Knowledge" text={selectedUser.knowledge} />
                  <NarrativeBlock ui={ui} isDark={isDark} title="Core values" text={selectedUser.core_values} />
                  <NarrativeBlock ui={ui} isDark={isDark} title="Focus" text={selectedUser.focus} />
                </Grid>
              </Grid>
            </DialogContent>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogActions sx={{ px: 3, py: 2.5, flexWrap: "wrap", gap: 1 }}>
              <Button
                startIcon={
                  isActiveStaff(selectedUser) ? (
                    <PersonOffOutlinedIcon />
                  ) : (
                    <HowToRegOutlinedIcon />
                  )
                }
                onClick={() => setStaffToDeactivate(selectedUser)}
                sx={{
                  ...outlineBtn(
                    isActiveStaff(selectedUser)
                      ? colors.orange[500]
                      : colors.green[500]
                  ),
                  mr: "auto",
                }}
              >
                {isActiveStaff(selectedUser) ? "Deactivate" : "Reactivate"}
              </Button>

              <Button
                startIcon={<VisibilityOutlinedIcon />}
                href={`/admin/dashboard/staff/${selectedUser.Id}`}
                sx={outlineBtn(ui.accent2)}
              >
                Full profile
              </Button>

              <Button
                startIcon={
                  isFlagged(selectedUser.isVerified) ? (
                    <AirplanemodeInactiveOutlinedIcon />
                  ) : (
                    <AirplanemodeActiveOutlinedIcon />
                  )
                }
                onClick={() => handleVerifyToggle(selectedUser)}
                sx={outlineBtn(
                  isFlagged(selectedUser.isVerified) ? colors.orange[500] : colors.green[500]
                )}
              >
                {isFlagged(selectedUser.isVerified) ? "Unverify" : "Verify"}
              </Button>

              <Button
                startIcon={<EditNoteOutlinedIcon />}
                onClick={() => {
                  const staff = selectedUser;
                  setSelectedUser(null);
                  handleOpenDialog(true, staff);
                }}
                sx={primaryBtn}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Add / Edit dialog                                               */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            {isEditing ? "Edit staff member" : "Add staff member"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1.5 }}>
            {/* Photo */}
            <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 3 }}>
              <Avatar
                src={imagePreview || ""}
                sx={{
                  width: 84,
                  height: 84,
                  fontSize: 26,
                  fontWeight: 700,
                  bgcolor: ui.surfaceAlt,
                  color: ui.textPrimary,
                  border: `1px solid ${ui.border}`,
                }}
              >
                {initials(formData)}
              </Avatar>
              <Box>
                <Button
                  component="label"
                  startIcon={<PhotoCameraOutlinedIcon />}
                  sx={outlineBtn(ui.accent)}
                >
                  {imagePreview ? "Change photo" : "Upload photo"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                <Typography variant="h6" sx={{ mt: 0.85, color: ui.textSecondary }}>
                  Optional. Square images look best.
                </Typography>
              </Box>
            </Stack>

            <FormSection ui={ui} title="Account">
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="First name"
                  name="FirstName"
                  value={formData.FirstName}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Last name"
                  name="LastName"
                  value={formData.LastName}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  name="Password"
                  value={formData.Password}
                  onChange={handleInputChange}
                  required={!isEditing}
                  helperText={isEditing ? "Leave blank to keep current password" : " "}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r} sx={{ textTransform: "capitalize" }}>
                      {r}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Account status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Verified"
                  name="isVerified"
                  value={formData.isVerified}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                >
                  <MenuItem value="0">Not verified</MenuItem>
                  <MenuItem value="1">Verified</MenuItem>
                </TextField>
              </Grid>
            </FormSection>

            <FormSection ui={ui} title="Personal">
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="tel"
                  label="Cell"
                  name="Cell"
                  value={formData.Cell}
                  onChange={handleInputChange}
                  placeholder="081XXXXXXX"
                  inputProps={{ maxLength: 15 }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Employee type"
                  name="employeeType"
                  value={formData.employeeType}
                  onChange={handleInputChange}
                  sx={fieldSx}
                >
                  {EMPLOYEE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Nationality"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="National ID"
                  name="national_id"
                  value={formData.national_id}
                  onChange={handleInputChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Birth day"
                  name="dobDay"
                  value={formData.date_of_birth[0].day}
                  onChange={handleInputChange}
                  inputProps={{ min: 1, max: 31 }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Birth month"
                  name="dobMonth"
                  value={formData.date_of_birth[0].month}
                  onChange={handleInputChange}
                  inputProps={{ min: 1, max: 12 }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Birth year"
                  name="dobYear"
                  value={formData.date_of_birth[0].year}
                  onChange={handleInputChange}
                  inputProps={{ min: 1900, max: currentYear }}
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>

            <FormSection ui={ui} title="Next of kin">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Full name"
                  name="nextOfKinFullname"
                  value={formData.next_of_kin[0].fullname}
                  onChange={handleInputChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  name="nextOfKinEmail"
                  value={formData.next_of_kin[0].email}
                  onChange={handleInputChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="tel"
                  label="Cell"
                  name="nextOfKinCell"
                  value={formData.next_of_kin[0].cell}
                  onChange={handleInputChange}
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>

            <FormSection ui={ui} title="Physical address">
              {["street", "city", "town", "country"].map((f) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f}>
                  <TextField
                    fullWidth
                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                    name={`physical${f.charAt(0).toUpperCase() + f.slice(1)}`}
                    value={formData.physicalAddress[0][f]}
                    onChange={handleInputChange}
                    sx={fieldSx}
                  />
                </Grid>
              ))}
            </FormSection>

            <FormSection ui={ui} title="Postal address">
              {["code", "city", "town", "country"].map((f) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f}>
                  <TextField
                    fullWidth
                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                    name={`postal${f.charAt(0).toUpperCase() + f.slice(1)}`}
                    value={formData.postalAddress[0][f]}
                    onChange={handleInputChange}
                    sx={fieldSx}
                  />
                </Grid>
              ))}
            </FormSection>

            <FormSection ui={ui} title="Profile">
              {[
                { name: "role_explanation", label: "Role explanation" },
                { name: "drive_quote", label: "Drive quote" },
                { name: "passion", label: "Passion" },
                { name: "knowledge", label: "Knowledge" },
                { name: "core_values", label: "Core values" },
                { name: "focus", label: "Focus" },
              ].map((f) => (
                <Grid size={{ xs: 12, sm: 6 }} key={f.name}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label={f.label}
                    name={f.name}
                    value={formData[f.name]}
                    onChange={handleInputChange}
                    sx={fieldSx}
                  />
                </Grid>
              ))}
            </FormSection>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} sx={primaryBtn}>
            {saving ? "Saving…" : isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Deactivate / reactivate dialog                                  */}
      {/* Staff are never deleted - deactivating keeps their leave, asset  */}
      {/* and task history intact and is reversible.                      */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!staffToDeactivate}
        onClose={() => setStaffToDeactivate(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            {staffToDeactivate && !isActiveStaff(staffToDeactivate)
              ? "Reactivate staff account"
              : "Deactivate staff account"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5" sx={{ color: ui.textSecondary, lineHeight: 1.6 }}>
            {staffToDeactivate && !isActiveStaff(staffToDeactivate) ? (
              <>
                Restore access for{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 800, color: ui.textPrimary }}
                >
                  {staffToDeactivate?.FirstName} {staffToDeactivate?.LastName}
                </Box>
                ? They will be able to sign in again and will reappear in
                assignment lists.
              </>
            ) : (
              <>
                Deactivate{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 800, color: ui.textPrimary }}
                >
                  {staffToDeactivate?.FirstName} {staffToDeactivate?.LastName}
                </Box>
                ? They will no longer be able to sign in and will drop out of
                assignment lists, but their leave, asset and task records stay
                intact. This can be undone at any time.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setStaffToDeactivate(null)}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeactivate}
            variant="contained"
            startIcon={
              staffToDeactivate && !isActiveStaff(staffToDeactivate) ? (
                <HowToRegOutlinedIcon />
              ) : (
                <PersonOffOutlinedIcon />
              )
            }
            sx={{
              px: 2.75,
              py: 1.1,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor:
                staffToDeactivate && !isActiveStaff(staffToDeactivate)
                  ? colors.green[500]
                  : colors.orange[500],
              "&:hover": {
                bgcolor:
                  staffToDeactivate && !isActiveStaff(staffToDeactivate)
                    ? colors.green[600]
                    : colors.orange[600],
              },
            }}
          >
            {staffToDeactivate && !isActiveStaff(staffToDeactivate)
              ? "Reactivate"
              : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Team;